import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  List,
  Button,
  Checkbox,
  Typography,
  Badge,
  Divider,
  Grid,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { InteractiveModal } from 'components/Modals/InteractiveModal/BaseModal';
import { groupedData, fineGrainGroupedData } from 'utils/helpers/tags';
import { capitalizeFirstLetter } from 'utils/helpers/string.manipulation';
import { ThemePalette } from 'mui.theme';
import { determineBadgeCount } from 'views/SensorAnalysisView/utils';
import {
  defaultTagSelectionLimit,
  tagSelectionLimitPerCustomer,
} from 'utils/helpers/general';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  title: string;
  open: boolean;
  handleAbort: () => void;
  enableDarkTheme: boolean;
  tags: any[];
  addTags: any;
  defaultTags: string[];
  dataType: string;
}

export const SelectTagModal = ({
  title,
  open,
  handleAbort,
  enableDarkTheme = false,
  tags,
  addTags,
  defaultTags,
  dataType,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const customerCode = useAppSelector((state) => state.persistedReducer)
    .customer.code;
  const [selectOptions, setSelectOption] = useState<any[]>([]);
  const [currentGroup, setCurrentGroup] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([...defaultTags]);
  const [tagOptions, setTagOptions] = useState<any[]>([]);
  const [value, setValue] = useState('');

  const handleChange = (event: any) => {
    setValue(event.target.value);
  };

  const customerTagLimt: number =
    tagSelectionLimitPerCustomer[customerCode] ?? defaultTagSelectionLimit;

  const showTagLimitWarning = () => {
    dispatch(
      showNotification({
        type: 'error',
        message: `This selection will exceed the maximum limit of ${customerTagLimt} tags.`,
      })
    );
  };

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  useEffect(() => {
    const tagsToWorkWith = [...tags];
    const tagOptionsWithName = tagsToWorkWith.map((item) => {
      return {
        id: item.tagName,
        display: item.tagAlias,
        tagName: item.tagName,
      };
    });

    const groupedTags: any[] = groupedData({
      selectorOptions: tagOptionsWithName,
    });

    const fineGrainGroupedTags: any = fineGrainGroupedData(tagOptionsWithName);

    const groupToUse =
      dataType === 'regular' ? groupedTags : fineGrainGroupedTags;

    const filteredTags = groupToUse.map((item: any) => ({
      name: item.groupName,
      tags: item.options,
    }));

    setSelectOption([...filteredTags]);
    setCurrentGroup(filteredTags[0]?.name);
  }, [tags]);

  useEffect(() => {
    if (currentGroup !== '') {
      const selectedGroup = selectOptions.find(
        (item) => item.name === currentGroup
      );

      const possibleTags = selectedGroup?.tags?.map((item: any) => {
        return { ...item };
      });
      setTagOptions(possibleTags);
    }
  }, [currentGroup]);

  useEffect(() => {
    if (value === '') {
      // Reset to first tag group when search field is cleared
      if (selectOptions.length > 0) {
        setCurrentGroup(selectOptions[0]?.name || '');
        const selectedGroup = selectOptions.find(
          (item) => item.name === selectOptions[0]?.name
        );
        const possibleTags = selectedGroup?.tags?.map((item: any) => {
          return { ...item };
        });
        setTagOptions(possibleTags || []);
      }
      return;
    }

    const allTags = [...tags].map((item) => item.tagAlias ?? item.tagName);

    // Split search string by spaces to get individual search terms
    const searchTerms = value
      .toLowerCase()
      .split(' ')
      .filter((term) => term.trim() !== '');

    // Filter tags that match ALL search terms (AND logic)
    const searchedTags = allTags.filter((item) => {
      const tagLowerCase = item.toLowerCase();
      // Check if all search terms are included in the tag
      return searchTerms.every((term) => tagLowerCase.includes(term));
    });

    // Map to the proper tag objects
    const possibleOptions = searchedTags.map((item) => {
      const itemDetails = tags.find((td) =>
        [td.tagAlias, td.tagName].includes(item)
      );
      return {
        id: itemDetails.tagName,
        tagName: itemDetails.tagName,
        display: itemDetails.tagAlias,
      };
    });

    setCurrentGroup('');
    setTagOptions(possibleOptions);
  }, [value, selectOptions, tags]);

  const handleTagClick = (tagName: string) => {
    setSelectedTags((prevSelectedTags) => {
      if (prevSelectedTags.includes(tagName)) {
        return prevSelectedTags.filter((item) => item !== tagName);
      } else {
        if ([...prevSelectedTags, tagName].length > customerTagLimt) {
          showTagLimitWarning();
          return [...prevSelectedTags];
        }
        return [...prevSelectedTags, tagName];
      }
    });
  };

  const handleTagGroupClick = (tagGroupName: string) => {
    setCurrentGroup(tagGroupName);
  };

  const selectAll = () => {
    const allTags = [...tags].map((item) => item.tagName);
    if (allTags.length > customerTagLimt) {
      showTagLimitWarning();
      return;
    }
    setSelectedTags(allTags);
  };

  const unSelectAll = () => {
    setSelectedTags([]);
    const allTags = [...tags].map((item) => item.tagName);
    const isSbIgnitionAvailable = allTags.find(
      (item) => item === 'sb_ignition_status'
    );
    if (isSbIgnitionAvailable) {
      setSelectedTags(['sb_ignition_status']);
    }
  };

  function isAllOptionsSelected() {
    const tagNames = tagOptions.map((item) => item.id);
    return tagNames.every((tag) => selectedTags.includes(tag));
  }

  function handleCategorySelectAll() {
    const tagNames = tagOptions.map((item) => item.id);
    const tagsToSelect = [...selectedTags, ...tagNames];
    if ([...new Set(tagsToSelect)].length > customerTagLimt) {
      showTagLimitWarning();
      return;
    }
    setSelectedTags([...new Set(tagsToSelect)]);
  }

  function handleCategoryUnSelectAll() {
    const tagNames = tagOptions.map((item) => item.id);
    const tagsToSelect = selectedTags.filter(
      (item) => !tagNames.includes(item)
    );
    setSelectedTags(tagsToSelect);
  }

  return (
    <InteractiveModal
      initialState={open}
      SuccessButtonProps={{
        disabled: false,
        onClick: () => {
          addTags([...new Set(selectedTags)]);
          handleAbort();
        },
        label: t('apply'),
      }}
      handleAbort={handleAbort}
      cancelButtonText={t('cancel')}
      title={title}
      enableDarkTheme={enableDarkTheme}
      ModalContentProps={{
        gridItemProps: {
          lg: 12,
        },
      }}
    >
      <List
        style={{
          display: 'flex',
          flexDirection: 'row',
          padding: 0,
          minWidth: '800px',
        }}
      ></List>
      <Box>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
          }}
        >
          <TextField
            label={t('search_for_a_tag')}
            variant="outlined"
            InputProps={{
              sx: {
                color: enableDarkTheme
                  ? ThemePalette.typography.wheat
                  : ThemePalette.typography.black,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#38475E',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#38475E',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#38475E',
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: enableDarkTheme
                  ? ThemePalette.typography.wheat
                  : ThemePalette.typography.black,
                '&.Mui-focused': {
                  color: enableDarkTheme
                    ? ThemePalette.typography.wheat
                    : ThemePalette.typography.black,
                },
              },
            }}
            sx={{
              marginBottom: '15px',
              borderRadius: '4px',
              width: '70%',
              '& .MuiInputBase-input::placeholder': {
                color: 'gray',
              },
            }}
            value={value}
            onChange={handleChange} // Track the value in state
            onKeyDown={handleKeyDown} // Prevent Enter key from submitting or doing any action
          />
          <Box
            sx={{
              alignItems: 'center',
              textAlign: 'center',
              marginLeft: '20px',
            }}
          >
            <Typography
              sx={{
                color: palette.textPrimary,
              }}
            >
              {selectedTags.length} {t('tags_selected')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
              }}
            >
              <Button
                sx={{
                  color: palette.primary,
                  textTransform: 'none',
                }}
                onClick={() => {
                  unSelectAll();
                }}
              >
                {t('deselect_all')}
              </Button>
              <Button
                sx={{
                  color: palette.primary,
                  textTransform: 'none',
                }}
                onClick={() => {
                  selectAll();
                }}
              >
                {t('select_all')}
              </Button>
            </Box>
          </Box>
        </Box>
        <Divider
          sx={{
            backgroundColor: '#4b535E',
          }}
        />
        {selectOptions.length > 0 ? (
          <Box>
            <Grid
              container
              sx={{
                minHeight: '350px',
                maxHeight: '350px',
                borderBottom: '1px solid #4b535E',
              }}
            >
              <Grid
                item
                lg={4}
                sx={{
                  borderRight: '1px solid #4b535E',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                  }}
                >
                  <Box
                    sx={{
                      color: 'white',
                      width: '100%',
                      height: '100%',
                      maxHeight: '350px', // Set a maximum height for scrolling
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {selectOptions.map((tag, index) => (
                      <React.Fragment key={index}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '10px',
                            cursor: 'pointer',
                            backgroundColor:
                              currentGroup === tag.name
                                ? enableDarkTheme
                                  ? '#09182D'
                                  : '#1664c0'
                                : '',
                            '&:hover': {
                              backgroundColor: enableDarkTheme
                                ? currentGroup === tag.name
                                  ? '#09182D'
                                  : '#1D2738'
                                : currentGroup === tag.name
                                ? '#1664c0'
                                : '#1664c055',
                            },
                            minHeight: '40px',
                            borderBottom: '1px solid #4b535E',
                          }}
                          onClick={() => {
                            handleTagGroupClick(tag.name);
                          }}
                        >
                          <Typography
                            sx={{
                              color:
                                currentGroup === tag.name
                                  ? ThemePalette.typography.white
                                  : palette.textPrimary,
                              '&:hover': {
                                color: ThemePalette.typography.white,
                              },
                              fontSize: '12px',
                            }}
                          >
                            <Badge
                              badgeContent={determineBadgeCount(
                                tag.name,
                                selectedTags,
                                selectOptions
                              )}
                              color="primary"
                              sx={{
                                '& .MuiBadge-badge': { right: -10, top: 5 },
                              }}
                            >
                              {t(
                                `tag_group_${tag.name
                                  .toLowerCase()
                                  .replace(/[\s-]/g, '_')}`,
                                {
                                  defaultValue: capitalizeFirstLetter(tag.name),
                                }
                              )}
                            </Badge>
                          </Typography>
                          <ArrowForwardIosIcon
                            sx={{
                              color: palette.textPrimary,
                            }}
                          />
                        </Box>
                        <Divider sx={{ backgroundColor: '#1D2738' }} />
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              </Grid>
              <Grid item lg={8}>
                <Box
                  sx={{
                    padding: '10px',
                    paddingLeft: '20px',
                    height: '100%',
                    maxHeight: '330px', // Set a maximum height for scrolling
                    overflowY: 'auto',
                  }}
                >
                  <FormGroup>
                    {tagOptions.length > 0 && (
                      <>
                        <FormControlLabel
                          key="Select-all-cat"
                          control={
                            <Checkbox
                              checked={isAllOptionsSelected()}
                              onChange={() => {
                                isAllOptionsSelected()
                                  ? handleCategoryUnSelectAll()
                                  : handleCategorySelectAll();
                              }}
                              sx={{
                                alignSelf: 'center',
                                color: enableDarkTheme
                                  ? palette.primary
                                  : ThemePalette.typography.primary,
                                '&.Mui-checked': {
                                  color: enableDarkTheme
                                    ? palette.primary
                                    : ThemePalette.typography.primary,
                                },
                                '&.Mui-disabled': {
                                  color: ThemePalette.typography.secondary,
                                },
                              }}
                            />
                          }
                          label={
                            isAllOptionsSelected()
                              ? 'UnSelect All'
                              : 'Select All'
                          }
                          sx={{
                            color: palette.textPrimary,
                          }}
                        />
                        {tagOptions.map((tag: any, index: number) =>
                          tag.isHeader ? (
                            <Typography
                              key={index}
                              sx={{
                                color: enableDarkTheme
                                  ? palette.primary
                                  : ThemePalette.typography.primary,
                                fontWeight: 'bold',
                                mt: 2,
                                mb: 1,
                                borderBottom: '1px solid',
                                borderColor: enableDarkTheme
                                  ? palette.primary
                                  : ThemePalette.typography.primary,
                                paddingBottom: '4px',
                              }}
                            >
                              {tag.display}
                            </Typography>
                          ) : (
                            <FormControlLabel
                              key={index}
                              control={
                                <Checkbox
                                  checked={selectedTags.includes(tag.tagName)}
                                  onChange={() => {
                                    handleTagClick(tag.tagName);
                                  }}
                                  sx={{
                                    alignSelf: 'center',
                                    color: enableDarkTheme
                                      ? palette.primary
                                      : ThemePalette.typography.primary,
                                    '&.Mui-checked': {
                                      color: enableDarkTheme
                                        ? palette.primary
                                        : ThemePalette.typography.primary,
                                    },
                                    '&.Mui-disabled': {
                                      color: ThemePalette.typography.secondary,
                                    },
                                  }}
                                />
                              }
                              label={tag.display}
                              sx={{
                                color: palette.textPrimary,
                              }}
                            />
                          )
                        )}
                      </>
                    )}
                  </FormGroup>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: '12px',
              paddingTop: '10px',
              color: palette.textPrimary,
            }}
          >
            {t('no_available_tags_for_selected_asset')}
          </Typography>
        )}
      </Box>
    </InteractiveModal>
  );
};
